import { MobileBottomNav } from "@/components/mobile-bottom-nav";
import { MobileMarketHeader } from "@/components/mobile-market-header";
import { MobileTradeView } from "@/components/mobile-trade-view";
import { Toaster } from "@/components/ui/sonner";
import { AccountPanel } from "@/features/margin-panel/account-panel";
import { OrderBookPanel } from "@/features/order-book/order-book-panel";
import { OrderFormPanel } from "@/features/order-form/order-form-panel";
import { MarketChartPanel } from "@/features/market-chart/market-chart-panel";
import { OrderFormProvider } from "@/features/order-form/order-form-provider";
import { ProductProvider } from "@/features/product/product-provider";
import { ProductUrlSync } from "@/features/product/product-url-sync";
import { PositionsPanel } from "@/features/positions/positions-panel";
import { AccountPage } from "@/features/account/account-page";
import { LPDashboardPage } from "@/features/lp-dashboard/lp-dashboard-page";
import { TradingAnalysisPage } from "@/features/trading-analysis";
import { Header } from "@/header/header";
import { type PanelConfig } from "@/layout/layout-active-context";
import { ActiveLayoutProvider } from "@/layout/layout-active-provider";
import { LayoutListProvider } from "@/layout/layout-list-provider";
import { PanelLayout } from "@/layout/layout";
import type { Layout } from "@/layout/layout-utils";
import { cn } from "@/lib/utils";
import { useLayouts, useSelectedLayoutId } from "@/state/state-storage";
import {
  createMobileLayout,
  type MobileLayoutView,
} from "@/state/state-default";
import { ThemeProvider } from "@/theme/theme-provider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { ClientLocationProvider } from "@/client-location/client-location-provider";
import { SolanaWalletProvider } from "@/solana/wallet-provider";
import { useMarketPriceSync } from "@/solana/hooks/use-market-price-sync";
import { useOracleSync } from "@/solana/hooks/use-oracle-sync";
import { useMarkPriceSync } from "@/solana/hooks/use-mark-price-sync";
import { useMarketStatsSync } from "@/backend/hooks/use-market-stats-sync";
import { TradingSettingsProvider } from "@/features/trading-settings/trading-settings-provider";
import { StatusBar } from "@/components/status-bar";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useBackendEvents } from "@/backend";
import { useAccountSync } from "@/backend/hooks/use-account-sync";
import { AuthProvider } from "@/backend/auth-provider";
import { useMarketSEO } from "@/hooks/use-market-seo";
import { MarketsProvider } from "@/domains/markets";
import { AffiliateInfo } from "@/routes/affiliate-info";
import { ReferralsInfo } from "@/routes/referrals-info";
import { PrivyAuthProvider } from "@/backend/privy-auth-provider";
import { UnifiedAuthProvider } from "@/backend/unified-auth-provider";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { NotFound } from "@/routes/not-found";
import { InvitePage } from "@/routes/invite";
import { wsClient } from "@/backend/ws-client";
import { AffiliatePage, BecomeAffiliatePage } from "@/features/affiliate";
import { ReferralsPage } from "@/features/referrals/referrals-page";
import { useReferralTracking } from "@/features/affiliate/hooks/use-referral-tracking";
import { useAffiliateReferralLinking } from "@/features/affiliate/hooks/use-affiliate-referral-linking";
import {
  OnboardingProvider,
  WelcomeModal,
  TourOverlay,
  TourMobile,
} from "@/features/onboarding";
import { FavoritesProvider } from "@/features/favorites/favorites-context";
import { MaintenanceModal } from "@/components/maintenance-modal";
import { useMaintenanceMode } from "@/hooks/use-maintenance-mode";
import { NotificationProvider } from "@/features/notifications/notification-context";
import { NotificationSheet } from "@/features/notifications/notification-sheet";
import { NotificationPreferencesModal } from "@/features/notifications/notification-preferences-modal";
import { PersistentNotificationContainer, usePersistentNotifications } from "@/components/persistent-notification";
import { I18nProvider } from "@/features/i18n";

// Component that subscribes to backend WebSocket events and syncs account data
const BackendEventsSubscriber = () => {
  const { userId, isAuthenticated, isReady, accessToken } = useUnifiedAuth();
  
  // Set WebSocket token from unified auth (works for both wallet JWT and Supabase token)
  useEffect(() => {
    wsClient.setToken(accessToken);
  }, [accessToken]);
  
  // Only sync when auth is READY, authenticated, AND have valid token
  // This prevents race conditions during page refresh when Privy is initializing
  // Without waiting for isReady, components render with no auth then re-render when auth loads
  const syncUserId = isReady && isAuthenticated && userId && accessToken ? userId : undefined;
  
  // This hook syncs account data (positions, orders, balances) via HTTP + WebSocket
  useAccountSync(syncUserId);
  
  // This hook establishes WebSocket connection and handles deposit events
  useBackendEvents(userId || undefined);
  
  return null; // This component doesn't render anything visible
};

// Component that renders persistent notifications
const PersistentNotificationsRenderer = () => {
  const { notifications, dismissNotification } = usePersistentNotifications();
  return (
    <PersistentNotificationContainer
      notifications={notifications}
      onDismiss={dismissNotification}
    />
  );
};

// Desktop panel config with fixed widths
const desktopConfig: Record<string, PanelConfig> = {
  "order-form": {
    component: OrderFormPanel,
    name: "Order Form",
    enableHeader: false,
    enableMaximize: false,
    minWidth: 330,
    maxWidth: 330,
    minHeight: 545,
  },
  "order-book": {
    component: OrderBookPanel,
    name: "Order Book",
    enableHeader: false,
    minWidth: 240,
    maxWidth: 240,
  },
  account: {
    component: AccountPanel,
    name: "Account",
    enableHeader: false,
    minWidth: 330,
    maxWidth: 330,
    minHeight: 175,
    maxHeight: 175,
  },
  "market-chart": {
    component: MarketChartPanel,
    name: "Market Chart",
    enableHeader: false,
    enableMaximize: false,
  },
  positions: {
    component: PositionsPanel,
    name: "Positions",
    enableHeader: false,
    enableMaximize: false,
  },
};

// Mobile panel config: order book fixed small, order form flexible
const mobileConfig: Record<string, PanelConfig> = {
  "order-form": {
    component: OrderFormPanel,
    name: "Order Form",
    enableHeader: false,
    enableMaximize: false,
    minWidth: 180,
    // No maxWidth - let it flex to fill remaining space
  },
  "order-book": {
    component: OrderBookPanel,
    name: "Order Book",
    enableHeader: false,
    minWidth: 155,
    maxWidth: 155,
  },
  account: {
    component: AccountPanel,
    name: "Account",
    enableHeader: false,
  },
  "market-chart": {
    component: MarketChartPanel,
    name: "Market Chart",
    enableHeader: false,
    enableMaximize: false,
  },
  positions: {
    component: PositionsPanel,
    name: "Positions",
    enableHeader: false,
    enableMaximize: false,
  },
};

// Component that provides onboarding context and overlays
const OnboardingWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <OnboardingProvider>
      {children}
      <WelcomeModal />
      <TourOverlay />
      <TourMobile />
    </OnboardingProvider>
  );
};

// Trading page content (the original main view)
const TradingPage = () => {
  const [layouts, setLayouts] = useLayouts();
  const [selectedLayoutId, setSelectedLayoutId] = useSelectedLayoutId();
  const location = useLocation();
  const initialView = (location.state as { mobileView?: MobileLayoutView })?.mobileView || "markets";
  const [mobileView, setMobileView] = useState<MobileLayoutView>(initialView);
  
  // Sync mobile view when navigating with state (e.g., from Portfolio → Trade)
  // Only runs on actual navigation (location.key changes), NOT on manual button clicks
  useEffect(() => {
    const navState = location.state as { mobileView?: MobileLayoutView };
    if (navState?.mobileView) {
      setMobileView(navState.mobileView);
    }
  }, [location.key]); // location.key changes on navigation, stable on button clicks
  
  const isMobile = useMediaQuery("(max-width: 1000px)");
  const navigate = useNavigate();
  const { data: maintenanceMode } = useMaintenanceMode();

  // Sync URL parameter with product context
  ProductUrlSync();

  // Update SEO meta tags for current market (title, description, structured data)
  useMarketSEO();

  // Handle mobile nav changes - navigate to /account for "account"
  const handleMobileViewChange = useCallback(
    (view: MobileLayoutView) => {
      if (view === "account") {
        navigate("/account");
      } else {
        setMobileView(view);
      }
    },
    [navigate]
  );
  const mobileLayout = useMemo(
    () => createMobileLayout(mobileView),
    [mobileView]
  );
  const noopSetLayouts = useCallback(
    (_: Layout[] | ((value: Layout[]) => Layout[])) => {},
    []
  );
  const noopSetSelectedLayoutId = useCallback(
    (_: string | ((value: string) => string)) => {},
    []
  );
  const layoutsToRender = isMobile ? [mobileLayout] : layouts;
  const setLayoutsHandler = isMobile ? noopSetLayouts : setLayouts;
  const selectedLayoutIdToUse = isMobile
    ? mobileLayout.data.id
    : selectedLayoutId;
  const setSelectedLayoutIdHandler = isMobile
    ? noopSetSelectedLayoutId
    : setSelectedLayoutId;
  const panelLayoutKey = isMobile
    ? `mobile-${mobileLayout.data.id}`
    : `desktop-${selectedLayoutId}`;
  
  // Use mobile or desktop config based on screen size
  const panelConfig = isMobile ? mobileConfig : desktopConfig;

  return (
    <LayoutListProvider
      layouts={layoutsToRender}
      setLayouts={setLayoutsHandler}
      selectedLayoutId={selectedLayoutIdToUse}
      setSelectedLayoutId={setSelectedLayoutIdHandler}
    >
      <ActiveLayoutProvider config={panelConfig}>
        <OrderFormProvider>
          <OnboardingWrapper>
            <div
              className={cn(
                "flex flex-col min-h-screen",
                isMobile ? "pb-14" : "pb-8"
              )}
            >
              <Header />

              {/* Mobile Trade Header - shows market selector and price on Trade view */}
              {isMobile && mobileView === "trade" && <MobileMarketHeader />}

              <div className="relative flex-1">
                {/* Use custom MobileTradeView for mobile trade view (content-driven height) */}
                {isMobile && mobileView === "trade" ? (
                  <MobileTradeView />
                ) : (
                  <PanelLayout
                    key={panelLayoutKey}
                    disablePanelControls={isMobile}
                  />
                )}
              </div>
            </div>

            {isMobile && (
              <MobileBottomNav
                activeView={mobileView}
                onChange={handleMobileViewChange}
              />
            )}

            <Toaster />
            <PersistentNotificationsRenderer />
            {!isMobile && <StatusBar />}

            {/* Maintenance Mode Modal - shows over everything */}
            {maintenanceMode?.enabled && (
              <MaintenanceModal message={maintenanceMode.message} />
            )}
          </OnboardingWrapper>
        </OrderFormProvider>
      </ActiveLayoutProvider>
    </LayoutListProvider>
  );
};

// Portfolio page wrapper
const AccountPageWrapper = () => {
  const isMobile = useMediaQuery("(max-width: 1000px)");
  const navigate = useNavigate();

  // Handle mobile nav changes - navigate back to trading page for other views
  const handleMobileViewChange = useCallback(
    (view: MobileLayoutView) => {
      if (view === "account") {
        // Already on portfolio/account page
        return;
      }
      // Navigate directly to /trade/BTC-USD to preserve state (/ redirects and loses state)
      navigate("/trade/BTC-USD", { state: { mobileView: view } });
    },
    [navigate]
  );

  return (
    <>
      <div className={cn("flex flex-col min-h-screen", isMobile ? "pb-14" : "")}>
        <Header />
        <AccountPage />
        <Toaster />
      </div>
      {isMobile && (
        <MobileBottomNav
          activeView="account"
          onChange={handleMobileViewChange}
        />
      )}
    </>
  );
};

// Affiliate page wrapper
const AffiliatePageWrapper = () => {
  const isMobile = useMediaQuery("(max-width: 1000px)");
  const navigate = useNavigate();

  // Handle mobile nav changes - navigate back to trading page for other views
  const handleMobileViewChange = useCallback(
    (view: MobileLayoutView) => {
      if (view === "account") {
        navigate("/account");
      } else {
        // Navigate directly to /trade/BTC-USD to preserve state (/ redirects and loses state)
        navigate("/trade/BTC-USD", { state: { mobileView: view } });
      }
    },
    [navigate]
  );

  return (
    <>
      <div className={cn("flex flex-col min-h-screen", isMobile ? "pb-14" : "")}>
        <Header />
        <AffiliatePage />
        <Toaster />
      </div>
      {isMobile && (
        <MobileBottomNav
          activeView="markets"
          onChange={handleMobileViewChange}
        />
      )}
    </>
  );
};

// Join page wrapper (for vanity URLs like /join/username)
const ReferralsPageWrapper = () => {
  const isMobile = useMediaQuery("(max-width: 1000px)");
  const navigate = useNavigate();
  const [activeView] = useState<MobileLayoutView>("account");

  // Handle mobile nav changes - navigate back to trading page for other views
  const handleMobileViewChange = useCallback(
    (view: MobileLayoutView) => {
      if (view === "account") {
        navigate("/account");
      } else {
        // Navigate directly to /trade/BTC-USD to preserve state (/ redirects and loses state)
        navigate("/trade/BTC-USD", { state: { mobileView: view } });
      }
    },
    [navigate]
  );

  return (
    <>
      <Header />
      <ReferralsPage />
      {isMobile && <MobileBottomNav activeView={activeView} onChange={handleMobileViewChange} />}
    </>
  );
};

const LPDashboardPageWrapper = () => {
  const isMobile = useMediaQuery("(max-width: 1000px)");
  const navigate = useNavigate();
  const [activeView] = useState<MobileLayoutView>("account");

  // Handle mobile nav changes - navigate back to trading page for other views
  const handleMobileViewChange = useCallback(
    (view: MobileLayoutView) => {
      if (view === "account") {
        navigate("/account");
      } else {
        // Navigate directly to /trade/BTC-USD to preserve state (/ redirects and loses state)
        navigate("/trade/BTC-USD", { state: { mobileView: view } });
      }
    },
    [navigate]
  );

  return (
    <>
      <Header />
      <LPDashboardPage />
      {isMobile && <MobileBottomNav activeView={activeView} onChange={handleMobileViewChange} />}
    </>
  );
};

// Trading Analysis page wrapper
const TradingAnalysisPageWrapper = () => {
  const isMobile = useMediaQuery("(max-width: 1000px)");
  const navigate = useNavigate();

  // Handle mobile nav changes
  const handleMobileViewChange = useCallback(
    (view: MobileLayoutView) => {
      if (view === "account") {
        navigate("/account");
      } else {
        navigate("/trade/BTC-USD", { state: { mobileView: view } });
      }
    },
    [navigate]
  );

  return (
    <>
      <div className={cn("flex flex-col min-h-screen", isMobile ? "pb-14" : "")}>
        <Header />
        <TradingAnalysisPage />
        <Toaster />
      </div>
      {isMobile && (
        <MobileBottomNav
          activeView="account"
          onChange={handleMobileViewChange}
        />
      )}
    </>
  );
};

const JoinPageWrapper = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Redirect /join/:slug to /invite/:slug for the personalized landing page
  useEffect(() => {
    if (slug) {
      navigate(`/invite/${slug}`, { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  }, [navigate, slug]);

  return null;
};

const AppContent = () => {
  // Initialize market configurations
  useMarketPriceSync();

  // Sync oracle prices from backend (Pyth - for funding only)
  useOracleSync();
  
  // Sync mark prices from backend (Provider - for trading/display)
  useMarkPriceSync();

  // Sync market stats (volume, OI, funding) via single WebSocket subscription
  useMarketStatsSync();

  // Track referral codes from URL (for user referrals)
  useReferralTracking();

  // Link affiliate referrals on authentication (from /invite/:slug pages)
  useAffiliateReferralLinking();

  return (
    <FavoritesProvider>
      <ProductProvider>
        <I18nProvider>
          <NotificationProvider>
            <BackendEventsSubscriber />
            <NotificationSheet />
            <NotificationPreferencesModal />
            <TradingSettingsProvider>
              <Routes>
            <Route path="/" element={<Navigate to="/trade/BTC-USD" replace />} />
            <Route path="/trade" element={<Navigate to="/trade/BTC-USD" replace />} />
            <Route path="/trade/:market" element={<TradingPage />} />
          <Route path="/account" element={<AccountPageWrapper />} />
          <Route path="/performance-lab" element={<TradingAnalysisPageWrapper />} />
          <Route path="/referrals" element={<ReferralsPageWrapper />} />
          <Route path="/affiliate" element={<AffiliatePageWrapper />} />
          <Route path="/become-affiliate" element={<BecomeAffiliatePage />} />
          <Route path="/lp-dashboard" element={<LPDashboardPageWrapper />} />
          <Route path="/join/:slug" element={<JoinPageWrapper />} />
          <Route path="/invite/:slug" element={<InvitePage />} />
          <Route path="/referrals-info" element={<ReferralsInfo />} />
          <Route path="/affiliate-info" element={<AffiliateInfo />} />
            {/* Catch-all route for 404 - Must be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
            </TradingSettingsProvider>
          </NotificationProvider>
        </I18nProvider>
      </ProductProvider>
    </FavoritesProvider>
  );
};

export const App = () => {
  // Industry standard for WebSocket-driven trading platforms:
  // - Initial HTTP fetch for data
  // - WebSocket for real-time updates (handled by useAccountSync)
  // - Minimal refetching (WebSocket keeps data fresh)
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        // Suppress console errors for expected 404s (e.g., LP accounts for non-LP users)
        // Errors are still handled in UI, just not logged to console
        retry: 1,
        retryDelay: 1000,
        // Use meta to mark queries that should suppress error logging
        meta: {
          suppressErrorLog: false,
        },
      },
    },
  }));

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <PrivyAuthProvider>
          <SolanaWalletProvider>
            <ClientLocationProvider>
              <MarketsProvider>
                <AuthProvider>
                  <UnifiedAuthProvider>
                    <AppContent />
                  </UnifiedAuthProvider>
                </AuthProvider>
              </MarketsProvider>
            </ClientLocationProvider>
          </SolanaWalletProvider>
        </PrivyAuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

